<script setup>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment-timezone'
import { apiConfigurationGetApiConfig, apiConfigurationUpdateApiConfig } from '@/api/cfes/apiConfiguration'
import { toast } from '@/utils/toast'

const store = useStore()

const apiConfigurationHeaders = [
  { title: 'API Name', key: 'apiName', sortable: false },
  { title: 'HTTP Method', key: 'httpMethod', sortable: false },
  { title: 'URI Prefix', key: 'uriPrefix', sortable: false },
  { title: 'Created Time (UTC)', key: 'createdTime', sortable: false },
  { title: 'Updated Time (UTC)', key: 'updatedTime', sortable: false },
]

const editable = ref(true)
const loading = ref(false)
const apiConfigurationSet = ref([])

const formatDate = (value) => (value ? moment.utc(value).format('YYYY-MM-DD HH:mm:ss') : '')

const listAll = async () => {
  loading.value = true
  try {
    const res = await apiConfigurationGetApiConfig()
    const list = (res || []).map((item) => ({
      ...item,
      apiCfg: typeof item.apiCfg === 'string' ? JSON.parse(item.apiCfg || '{}') : item.apiCfg || { requiredClaims: [] },
    }))
    apiConfigurationSet.value = list
  } catch (err) {
    console.error('Failed to load api configuration', err)
  } finally {
    loading.value = false
  }
}

const addClaim = (item) => {
  if (!item.apiCfg.requiredClaims) item.apiCfg.requiredClaims = []
  const next = item.apiCfg.requiredClaims.length + 1
  item.apiCfg.requiredClaims.push({ claimKey: `key${next}`, claimValue: `value${next}` })
}

const delClaim = (item, index) => {
  item.apiCfg.requiredClaims.splice(index, 1)
}

const updateApiCfg = async (item) => {
  const payload = { ...item, apiCfg: JSON.stringify(item.apiCfg || {}) }
  try {
    await apiConfigurationUpdateApiConfig(payload)
    toast.success('Required claims update succeed!')
  } catch (err) {
    console.error('Failed to update api config', err)
    toast.error('Required claims update failed!')
  }
}

const persistPreferred = (username) => {
  store.commit('SET_PREFERRED_USERNAME', username)
}

onMounted(() => {
  editable.value = true
  listAll()
})
</script>

<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">API Configuration</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="section-card">
          <v-card-title class="section-title d-flex align-center justify-space-between">
            <span>Token validation configuration</span>
            <v-progress-circular v-if="loading" indeterminate size="20" width="3" color="primary" />
          </v-card-title>
          <v-data-table
            dense
            :headers="apiConfigurationHeaders"
            :items="apiConfigurationSet"
            :items-per-page="-1"
            item-key="id"
            class="elevation-2 monitoring-table"
            show-expand
            disable-sort
            disable-pagination
            hide-default-footer
          >
            <template #expanded-row="{ item, columns }">
              <tr>
                <td :colspan="columns.length">
                  <v-card class="claimsPanel ma-2">
                    <v-container class="claimsTbl">
                      <v-row class="mb-2">
                        <v-col cols="3" class="font-weight-medium">Preferred Username</v-col>
                        <v-col class="d-flex align-center">
                          {{ item.preferredUsername }}
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="1" align="right">
                          <v-btn v-if="editable" small @click="addClaim(item)">Add</v-btn>
                        </v-col>
                        <v-col cols="2">Claim Key</v-col>
                        <v-col cols="6">Claim Value</v-col>
                      </v-row>
                      <v-row v-for="(c, idx) in item.apiCfg.requiredClaims" :key="idx">
                        <v-col cols="1" align="right">
                          <v-btn v-if="editable" small @click="delClaim(item, idx)">Del</v-btn>
                        </v-col>
                        <v-col cols="2">
                          <v-text-field :readonly="!editable" dense hide-details v-model="c.claimKey" />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field :readonly="!editable" dense hide-details v-model="c.claimValue" />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="1"></v-col>
                        <v-col cols="2">
                          <v-btn v-if="editable" small @click="updateApiCfg(item)">Update</v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card>
                </td>
              </tr>
            </template>
            <template #item.createdTime="{ item }">{{ formatDate(item.createdTime) }}</template>
            <template #item.updatedTime="{ item }">{{ formatDate(item.updatedTime) }}</template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.page-shell {
  padding: 8px 18px 28px;
}

.title {
  font-size: 1.1rem;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  padding: 10px 12px 14px;
}

.section-title {
  padding: 6px 4px;
  font-weight: 700;
}

.monitoring-table :deep(.v-data-table__wrapper) {
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.monitoring-table :deep(tbody tr:nth-child(odd)) {
  background-color: #f9fafb;
}

.monitoring-table :deep(tbody tr:hover) {
  background-color: #eef2ff;
}

.claimsPanel {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}
</style>

